'use client';

import { useCallback, useEffect } from 'react';
import type { DashboardSectionKey } from 'constant/dashboardSections';
import { DASHBOARD_SECTION_BY_KEY } from 'constant/dashboardSections';
import { useGetSystemModulesQuery } from 'services/userProfileApi';

import { resolveSectionEnabled } from 'utils/helpers/dashboardNav';
import {
  hasSystemModulesFailed,
  markSystemModulesFailed
} from 'utils/helpers/systemModules';

import type { SystemModulesResponse } from 'types';

export interface DashboardSectionAccess {
  isSectionEnabled: (key: DashboardSectionKey) => boolean;
  sysModules: SystemModulesResponse | undefined;
  isLoading: boolean;
  isError: boolean;
  /**
   * True once sys-modules is known-unusable for this session: every section reverts to the
   * tenant's static config, exactly as it behaved before sys-modules existed.
   */
  isFallback: boolean;
  refetch: () => void;
}

/**
 * Per-tenant account-portal section visibility from `?meth=sys-modules`.
 * See `resolveSectionEnabled` for the fail-open policy.
 */
export const useDashboardSectionAccess = (): DashboardSectionAccess => {
  const {
    data: sysModules,
    isLoading,
    isError,
    refetch
  } = useGetSystemModulesQuery();

  // A 400, a timeout, a network failure or an unrecognised payload all land here. Latch it
  // so the rest of the session stops consulting sys-modules altogether.
  useEffect(() => {
    if (isError) {
      markSystemModulesFailed();
    }
  }, [isError]);

  const isFallback = isError || hasSystemModulesFailed();

  const isSectionEnabled = useCallback(
    (key: DashboardSectionKey) =>
      resolveSectionEnabled(
        DASHBOARD_SECTION_BY_KEY[key],
        sysModules,
        isLoading,
        isFallback
      ),
    [sysModules, isLoading, isFallback]
  );

  return {
    isSectionEnabled,
    sysModules,
    isLoading,
    isError,
    isFallback,
    refetch
  };
};
